import { useState, useEffect, useCallback } from 'react';

const SESSION_KEY = 'ck-admin-session';
const PASSWORD_KEY = 'ck-admin-pwd';
const EMAIL_KEY = 'ck-admin-email';

/** Base URL for the admin API.
 *  - In dev: leave empty, CRA proxy forwards /api/* to localhost:4000
 *  - In prod: set REACT_APP_API_URL=https://api.ckan.mx (no trailing slash) */
const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

async function sha256Hex(str) {
  const buf = new TextEncoder().encode(str);
  const hashBuf = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hashBuf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

function readSession() {
  try { return sessionStorage.getItem(SESSION_KEY) === '1'; } catch (e) { return false; }
}

function writeSession(authed, password, email) {
  try {
    if (authed) {
      sessionStorage.setItem(SESSION_KEY, '1');
      if (password) sessionStorage.setItem(PASSWORD_KEY, password);
      if (email)    sessionStorage.setItem(EMAIL_KEY, email);
    } else {
      sessionStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(PASSWORD_KEY);
      sessionStorage.removeItem(EMAIL_KEY);
    }
  } catch (e) { /* ignore */ }
}

/** Returns the password the admin used to log in — used as Bearer token
 *  for API calls. Valid only while the session is active. */
export function getAuthToken() {
  try { return sessionStorage.getItem(PASSWORD_KEY) || ''; } catch (e) { return ''; }
}

/** Returns the email of the currently-logged-in admin (for display). */
export function getAuthEmail() {
  try { return sessionStorage.getItem(EMAIL_KEY) || ''; } catch (e) { return ''; }
}

/** Try to log in via the backend first. If the backend is unreachable
 *  and a client-side REACT_APP_ADMIN_PASSWORD_HASH (+ optional email)
 *  exists, fall back to a local check. In local mode, changes only
 *  persist to localStorage — see eventsStorage. */
async function attemptLogin(email, password) {
  // Try server
  try {
    const r = await fetch(`${API_BASE}/api/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (r.status === 200) {
      const data = await r.json().catch(() => ({}));
      return { ok: true, mode: 'server', email: data.email || email };
    }
    if (r.status === 400) return { ok: false, mode: 'server', error: 'Email y contraseña requeridos' };
    if (r.status === 401) return { ok: false, mode: 'server', error: 'Credenciales inválidas' };
    if (r.status === 503) {
      // Server running but not configured — fall through to client check
    } else {
      return { ok: false, mode: 'server', error: `Servidor: HTTP ${r.status}` };
    }
  } catch (e) {
    // Network error — server probably not running. Fall through.
  }

  // Client-side fallback
  const clientHash  = (process.env.REACT_APP_ADMIN_PASSWORD_HASH || '').toLowerCase().trim();
  const clientEmail = (process.env.REACT_APP_ADMIN_EMAIL || '').toLowerCase().trim();
  if (!clientHash) {
    return { ok: false, mode: 'none', error: 'Servidor no disponible' };
  }
  const emailOk = !clientEmail || clientEmail === String(email || '').toLowerCase().trim();
  const hash = await sha256Hex(password);
  if (emailOk && hash === clientHash) return { ok: true, mode: 'local', email };
  return { ok: false, mode: 'local', error: 'Credenciales inválidas' };
}

export function useAdminAuth() {
  const [authed, setAuthed] = useState(readSession);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null); // 'server' | 'local' | null

  useEffect(() => {
    const onStorage = e => { if (e.key === SESSION_KEY) setAuthed(readSession()); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    setBusy(true);
    try {
      const result = await attemptLogin(email, password);
      if (result.ok) {
        writeSession(true, password, result.email || email);
        setAuthed(true);
        setMode(result.mode);
        return true;
      }
      setError(result.error || 'Error al iniciar sesión');
      setMode(result.mode || null);
      return false;
    } catch (err) {
      setError('Error al iniciar sesión');
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const logout = useCallback(() => {
    writeSession(false);
    setAuthed(false);
    setMode(null);
  }, []);

  return { authed, login, logout, error, busy, mode };
}
